import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useSessione } from './api/sessione.js';
import { BarraLaterale } from './componenti/BarraLaterale.js';
import { BarraSuperiore } from './componenti/BarraSuperiore.js';
import { Accesso } from './pagine/Accesso.js';
import { ScegliAzienda } from './pagine/ScegliAzienda.js';
import { allineaConProfilo, applicaLingua, t } from './i18n/lingua.js';
import { applicaAspetto, seguiSistema } from './tema.js';

/**
 * Le tre porte da attraversare, in ordine: entrare, scegliere l'azienda,
 * lavorare. Ognuna compare solo se serve — chi ha una sola azienda non vede
 * mai la seconda.
 *
 * Sul telefono il menu non ci sta accanto alla pagina, e allora si apre da
 * sopra: è un cassetto, non una colonna. Si chiude da solo appena si arriva
 * da qualche parte — restare aperto dopo aver scelto una voce è il modo più
 * veloce per coprire la pagina che si è appena chiesta.
 */
export function Applicazione() {
  const { data: sessione, isPending, error: guasto, refetch, isFetching } = useSessione();
  const percorso = useRouterState({ select: (s) => s.location.href });
  const indirizzo = useRouterState({ select: (s) => s.location.pathname });
  const naviga = useNavigate();
  const [menuAperto, setMenuAperto] = useState(false);
  const schermataFatta = useRef(false);

  // L'aspetto arriva dal profilo appena si sa chi è connesso; da lì in poi
  // resta anche nel browser, per non far lampeggiare la pagina al prossimo
  // avvio.
  useEffect(() => {
    if (sessione?.user.theme) applicaAspetto(sessione.user.theme);
  }, [sessione?.user.theme]);

  useEffect(() => seguiSistema(), []);
  useEffect(() => applicaLingua(), []);

  // la lingua del profilo vale su una postazione nuova: chi entra da un altro
  // computer ritrova quella che usa sempre
  useEffect(() => allineaConProfilo(sessione?.user.language), [sessione?.user.language]);

  /**
   * La schermata da cui si comincia.
   *
   * Vale **una volta sola**, all'arrivo: chi apre il programma e si ritrova
   * sugli ordini è quello che ha chiesto: chi poi clicca «Il mio lavoro» ci
   * deve restare, e non essere rispedito indietro dalla stessa preferenza.
   */
  // chi esce e lascia entrare un altro nella stessa scheda: la schermata di
  // partenza vale di nuovo, per chi entra adesso
  useEffect(() => {
    if (!sessione) schermataFatta.current = false;
  }, [sessione]);

  useEffect(() => {
    if (schermataFatta.current || !sessione) return;
    schermataFatta.current = true;
    const scelta = sessione.user.homeScreen;
    if (scelta && scelta !== '/' && indirizzo === '/') {
      void naviga({ to: scelta, replace: true });
    }
  }, [sessione, indirizzo, naviga]);

  useEffect(() => setMenuAperto(false), [percorso]);

  // col cassetto aperto la pagina sotto non deve scorrere: si scorre il menu
  useEffect(() => {
    document.body.classList.toggle('menu-aperto', menuAperto);
    return () => document.body.classList.remove('menu-aperto');
  }, [menuAperto]);

  // Esc chiude, come ogni altra cosa che si apre sopra la pagina
  useEffect(() => {
    if (!menuAperto) return;
    const tasto = (e: KeyboardEvent) => e.key === 'Escape' && setMenuAperto(false);
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, [menuAperto]);

  if (isPending) return <div className="attesa">{t('Un momento…')}</div>;
  if (guasto) {
    return (
      <div className="attesa" role="alert">
        <p>{t('Il servizio non risponde: la tua sessione è a posto, non serve rientrare.')}</p>
        <button className="btn btn-primary" disabled={isFetching} onClick={() => void refetch()}>
          {isFetching ? t('Un momento…') : t('Riprova')}
        </button>
      </div>
    );
  }
  // il link di ripristino della password vale anche con una sessione aperta
  // nello stesso browser: prima finiva su «Questo indirizzo non esiste»,
  // perché la schermata del ripristino sta nella pagina di accesso
  if (!sessione || indirizzo.startsWith('/reset-password')) return <Accesso />;
  if (sessione.companyId === null && sessione.user.companies.length > 0)
    return <ScegliAzienda sessione={sessione} />;

  return (
    <div className={menuAperto ? 'app app-menu-aperto' : 'app'}>
      <BarraLaterale onNaviga={() => setMenuAperto(false)} />
      {/* il velo sotto il cassetto: si tocca fuori e si chiude, che è il
          gesto che tutti provano per primo */}
      <button
        className="velo-menu"
        aria-label={t('Chiudi il menu')}
        tabIndex={menuAperto ? 0 : -1}
        onClick={() => setMenuAperto(false)}
      />
      <div className="main">
        <BarraSuperiore
          sessione={sessione}
          menuAperto={menuAperto}
          onMenu={() => setMenuAperto((v) => !v)}
        />
        {/* cambiando azienda la pagina riparte da zero: quello che teneva a
            mano (modifiche non salvate, selezioni, conferme aperte) era
            dell'azienda di prima */}
        <div className="page" key={sessione.companyId ?? 0}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
